import React, { useEffect, useState } from 'react'
import {
  Box, Typography, Switch, TextField, FormControlLabel, Paper, Divider, Stack,
} from '@mui/material'
import { DP } from '../../views/driverPayTheme'

// Pure controlled form for editing a PayoutConfig. The 7 options as cards.
// Parent passes `value` (current config) and `onChange` (next config object).
//
// Each option is fully independent and combinable. Tip pass-through is implicit
// for any per-order option (1, 2, 3, 4, 5, 6) — driver always keeps 100% of tip.

const OPTIONS = [
  {
    key: 'opt1',
    title: 'Hourly + tip pass-through',
    subtitle: 'Driver clocks in/out. Hourly wage credited on clock-out + 100% of customer tip on every delivery.',
    fields: [{ key: 'opt1HourlyRate', label: 'Hourly rate ($)', step: '0.01' }],
  },
  {
    key: 'opt2',
    title: 'Hourly + per-order bonus + tip',
    subtitle: 'Same as option 1 plus a flat bonus on every delivered order.',
    fields: [
      { key: 'opt2HourlyRate', label: 'Hourly rate ($)', step: '0.01' },
      { key: 'opt2PerOrderBonus', label: 'Per-order bonus ($)', step: '0.01' },
    ],
  },
  {
    key: 'opt3',
    title: 'Per-order flat fee + tip',
    subtitle: 'No hourly. Each delivery pays a fixed amount + 100% of tip.',
    fields: [{ key: 'opt3FlatFeePerOrder', label: 'Flat fee per order ($)', step: '0.01' }],
  },
  {
    key: 'opt4',
    title: 'Base + per-mile + tip',
    subtitle: 'DoorDash-style: a base fee plus a rate per mile of delivery distance, plus 100% of tip.',
    fields: [
      { key: 'opt4BaseFee', label: 'Base fee ($)', step: '0.01' },
      { key: 'opt4PerMileRate', label: 'Per-mile rate ($)', step: '0.01' },
    ],
  },
  {
    key: 'opt5',
    title: 'Percentage of delivery fee + tip',
    subtitle: 'Driver gets a % of the customer\'s delivery fee plus 100% of tip.',
    fields: [{ key: 'opt5PercentOfDeliveryFee', label: 'Percent (%)', step: '0.1', max: 100 }],
  },
  {
    key: 'opt6',
    title: 'Guaranteed minimum',
    subtitle: 'If the sum of options 1–5 (excluding tip) is below this floor, platform tops up to the floor.',
    fields: [{ key: 'opt6MinimumPerDelivery', label: 'Minimum per delivery ($)', step: '0.01' }],
  },
  {
    key: 'opt8',
    title: 'Stack bonus per extra order',
    subtitle: 'Adds a bonus for each additional order in a stacked trip. (1 order = no bonus, 2 orders = 1x bonus, etc.)',
    fields: [{ key: 'opt8BonusPerExtraOrder', label: 'Bonus per extra order ($)', step: '0.01' }],
  },
]

// appearance: omit for the original cards (super admin pages); 'ink' for the
// restaurant panel's numbered rows (Driver Payouts, Per-Driver Pay Override).
export default function PayoutConfigForm({ value, onChange, disabled, appearance }) {
  // Local state so we can apply onBlur. Parent gets every change.
  const [local, setLocal] = useState(value || {})
  useEffect(() => { setLocal(value || {}) }, [value])

  const update = (patch) => {
    const next = { ...local, ...patch }
    setLocal(next)
    onChange?.(next)
  }

  const fieldVal = (k) => (local[k] != null ? local[k] : '')

  if (appearance === 'ink') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {OPTIONS.map((opt, idx) => {
          const enabledKey = `${opt.key}Enabled`
          const enabled = !!local[enabledKey]
          return (
            <Box key={opt.key} sx={{
              borderRadius: '14px',
              bgcolor: enabled ? DP.paleAmber : '#FFFFFF',
              border: enabled ? `2px solid ${DP.text}` : `1px solid ${DP.border}`,
              transition: 'background-color 150ms ease, border-color 150ms ease',
            }}>
              {/* The header is the switch's label, so a click anywhere on it toggles.
                  1px less padding when on keeps the 2px border from shifting the row. */}
              <Box component="label" sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                p: enabled ? '15px 17px' : '16px 18px',
                cursor: disabled ? 'default' : 'pointer',
              }}>
                <Box aria-hidden sx={{
                  width: 36, height: 36, flexShrink: 0, borderRadius: '10px', display: 'grid', placeItems: 'center',
                  fontSize: 15, fontVariantNumeric: 'tabular-nums',
                  bgcolor: enabled ? DP.amber : DP.chipBg,
                  color: enabled ? DP.text : DP.secondary,
                  fontWeight: enabled ? 800 : 700,
                }}>
                  {idx + 1}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.35, color: DP.text }}>
                    {opt.title}
                  </Typography>
                  <Typography sx={{ fontSize: 14, lineHeight: 1.45, mt: '2px', color: enabled ? DP.body : DP.secondary }}>
                    {opt.subtitle}
                  </Typography>
                </Box>
                <Switch
                  checked={enabled}
                  disabled={disabled}
                  onChange={(e) => update({ [enabledKey]: e.target.checked })}
                />
              </Box>
              {enabled && opt.fields.length > 0 && (
                <Box sx={{
                  mx: '17px', borderTop: `1px solid ${DP.noteBorder}`, pt: 2, pb: '18px', pl: '52px',
                  display: 'flex', flexWrap: 'wrap', gap: 2,
                }}>
                  {opt.fields.map(f => (
                    <TextField
                      key={f.key}
                      label={f.label}
                      type="number"
                      size="small"
                      disabled={disabled}
                      inputProps={{ min: 0, max: f.max, step: f.step }}
                      value={fieldVal(f.key)}
                      onChange={(e) => {
                        const n = e.target.value === '' ? '' : parseFloat(e.target.value)
                        update({ [f.key]: n })
                      }}
                      sx={{ flex: '1 1 200px', maxWidth: 260, '& .MuiOutlinedInput-input': { fontSize: 17, fontWeight: 600 } }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    )
  }

  return (
    <Stack spacing={1.5}>
      {OPTIONS.map(opt => {
        const enabledKey = `${opt.key}Enabled`
        const enabled = !!local[enabledKey]
        return (
          <Paper key={opt.key} sx={{
            p: 2,
            border: '1px solid',
            borderColor: enabled ? '#16A34A' : '#E5E7EB',
            backgroundColor: enabled ? '#F0FDF4' : '#fff',
            transition: 'all .15s',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0F172A' }}>
                  {opt.title}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mt: 0.5 }}>
                  {opt.subtitle}
                </Typography>
              </Box>
              <Switch
                checked={enabled}
                disabled={disabled}
                onChange={(e) => update({ [enabledKey]: e.target.checked })}
              />
            </Box>
            {enabled && opt.fields.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  {opt.fields.map(f => (
                    <TextField
                      key={f.key}
                      label={f.label}
                      type="number"
                      size="small"
                      fullWidth
                      disabled={disabled}
                      inputProps={{ min: 0, max: f.max, step: f.step }}
                      value={fieldVal(f.key)}
                      onChange={(e) => {
                        const n = e.target.value === '' ? '' : parseFloat(e.target.value)
                        update({ [f.key]: n })
                      }}
                    />
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        )
      })}
    </Stack>
  )
}
